import type { DocumentTableDefinition } from '@gitbook/api-client';
import type { Block, Document, Fragment } from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';
import { type DiffStrategy, type DiffTreeChange, diffTrees } from '@gitbook/util-tree-merge';
import { assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import * as expandable from '../expandable';
import { columnSpecs } from '../table';
import {
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_EXPANDABLE,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    BLOCK_TABLE,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    BlockDiffChangeType,
    type BlockTableData,
    LEAF_BLOCKS,
    type TableDiff,
    type TableRecordValue,
} from '../types';
import {
    annotateAllTextWithDiff,
    annotateBySettingDiffData,
    annotateByWrappingWithDiff,
} from './annotate';
import { diffInlines } from './diffInlines';
import { diffTableDefinitions, diffTableHeaderOrTitleVisibility } from './diffTableUtils';
import { transformDiffKey, withDiffKeyScope } from './keys';
import { compareSlateNodes } from './utils';

const diffStrategy: DiffStrategy<Block> = {
    getId: (node) => node.key,
    getChildren: () => [],
    setChildren: (node) => node,
    compareItems: compareSlateNodes,
    annotateChange: annotateBlockChange,
};

/**
 * Compute diff between two containers of blocks.
 */
export function diffBlocks<T extends Block | Document | Fragment>(
    original: T,
    modified: T
): List<Block> {
    const result = withDiffKeyScope(() =>
        diffTrees(
            {
                before: original.nodes.toArray() as Block[],
                after: modified.nodes.toArray() as Block[],
            },
            diffStrategy
        )
    );

    return List.create(result.items);
}

function annotateBlockChange(node: Block, change: DiffTreeChange<Block>): Block[] {
    if (change.type === 'modified') {
        // If the block type changed, treat it as a deletion followed by an addition
        if (change.before.type !== change.item.type) {
            return [
                ...annotateBlockChange(change.before, {
                    type: 'deleted',
                    item: change.before,
                }),
                ...annotateBlockChange(node, {
                    type: 'added',
                    item: node,
                }),
            ];
        }

        if (node.type === BLOCK_EXPANDABLE) {
            return [
                annotateByWrappingWithDiff(
                    diffExpandableBlock(change.before, change.item),
                    BlockDiffChangeType.Modified
                ),
            ];
        }

        if (node.type === BLOCK_TABLE) {
            return [
                annotateByWrappingWithDiff(
                    diffTableBlock(change.before, change.item),
                    BlockDiffChangeType.Modified
                ),
            ];
        }

        // We always show modified void blocks as added/removed to make the diff more readable
        if (node.isVoid) {
            return [
                // If a void node has been moved, we don't need to show the "deleted" part
                // as there will already be an "deleted" change at the original position
                // otherwise there would be a conflict in the keys
                change.moved
                    ? undefined
                    : annotateByWrappingWithDiff(change.before, BlockDiffChangeType.Deleted),
                annotateByWrappingWithDiff(change.item, BlockDiffChangeType.Added),
            ].filter(filterOutNullable);
        }

        // For code lines that were modified + moved, we also don't need to show the "deleted" part
        if (node.type === BLOCK_CODE_LINE) {
            return [
                change.moved
                    ? undefined
                    : annotateBySettingDiffData(
                          transformDiffKey(change.before, BlockDiffChangeType.Deleted),
                          BlockDiffChangeType.Deleted
                      ),
                annotateBySettingDiffData(
                    transformDiffKey(change.item, BlockDiffChangeType.Added),
                    BlockDiffChangeType.Added
                ),
            ].filter(filterOutNullable);
        }

        // For text blocks, we diff the inside of the block
        if (LEAF_BLOCKS.includes(node.type)) {
            return [
                annotateByWrappingWithDiff(
                    node.merge({
                        nodes: diffInlines(change.before, change.item),
                    }),
                    BlockDiffChangeType.Modified
                ),
            ];
        }

        if (node.type === BLOCK_CODE || node.type === BLOCK_TABS) {
            return [
                annotateByWrappingWithDiff(
                    node.merge({
                        nodes: diffBlocks(change.before, change.item),
                    }),
                    // Tab container should only show they've been modified inside
                    node.type === BLOCK_CODE
                        ? BlockDiffChangeType.Modified
                        : BlockDiffChangeType.ModifiedInside
                ),
            ];
        }

        // We diff inside the container
        return [
            node.merge({
                nodes: diffBlocks(change.before, change.item),
            }),
        ];
    }

    switch (node.type) {
        case BLOCK_CODE_LINE:
            return [annotateBySettingDiffData(node, change.type)];
        case BLOCK_CODE:
            // For added and removed, show all lines as added/removed
            return [
                annotateByWrappingWithDiff(
                    node.merge({
                        nodes: node.nodes.map((line) =>
                            annotateBySettingDiffData(line, change.type)
                        ),
                    }),
                    change.type === 'added'
                        ? BlockDiffChangeType.Added
                        : BlockDiffChangeType.Deleted
                ),
            ];

        case BLOCK_LIST_UL:
        case BLOCK_LIST_OL:
        case BLOCK_LIST_TASKS:
        case BLOCK_LIST_ITEM:
        case BLOCK_PARAGRAPH:
        case BLOCK_TABS:
        case BLOCK_TABS_ITEM:
        case BLOCK_IMAGES:
            // // Wrap all lists elements with a diff block and prevent conflicts
            // // by regenerating all "removed" blocks and their children's keys.
            // // The matching "added" ones will keep their original keys.
            return [
                annotateByWrappingWithDiff(
                    annotateAllTextWithDiff(node, change.type),
                    change.type === 'added'
                        ? BlockDiffChangeType.Added
                        : BlockDiffChangeType.Deleted
                ),
            ];

        default:
            return [
                annotateByWrappingWithDiff(
                    node,
                    change.type === 'added'
                        ? BlockDiffChangeType.Added
                        : BlockDiffChangeType.Deleted
                ),
            ];
    }
}

/**
 * Diff an expandable block by diffing the title and body fragments.
 */
function diffExpandableBlock(original: Block, modified: Block): Block {
    const originalTitle = expandable.getTitleFragment(original);
    const modifiedTitle = expandable.getTitleFragment(modified);

    const titleNodes = diffBlocks(originalTitle, modifiedTitle);

    const originalBody = expandable.getBodyFragment(original);
    const modifiedBody = expandable.getBodyFragment(modified);

    const bodyNodes = diffBlocks(originalBody, modifiedBody);

    return original
        .setFragment(originalTitle.merge({ nodes: titleNodes }))
        .setFragment(originalBody.merge({ nodes: bodyNodes }));
}

/**
 * The table block diff is computed in 2 steps:
 * 1. Diff the fragments of the table block (e.g. `text`) using `diffBlocks`.
 * 2. For each cell, we check if the value has changed and if so,
 *    we add it to the `diff` map of the table data.
 */
export function diffTableBlock(before: Block, modified: Block): Block {
    const prevTableData = before.data.toFrozenObject() as BlockTableData;
    const tableData = modified.data.toFrozenObject() as BlockTableData;

    const diff: TableDiff = {
        recordValues: new Map(),
        definitions: new Map(),
        view: {},
    };

    diffTableHeaderOrTitleVisibility(prevTableData, tableData, diff);
    diffTableDefinitions(prevTableData, tableData, diff);

    const diffedFragments = modified.fragments.map((modifiedFragment, key) => {
        const beforeFragment = before.fragments.get(key);
        if (!beforeFragment) {
            return modifiedFragment;
        }

        return modifiedFragment.merge({ nodes: diffBlocks(beforeFragment, modifiedFragment) });
    });

    Object.entries(tableData.records).forEach(([recordKey, record]) => {
        Object.entries(record.values).forEach(([columnKey, cellValue]) => {
            const prevColumnDef = prevTableData.definition[columnKey] as
                | DocumentTableDefinition
                | undefined;
            const prevCellValue = prevTableData.records[recordKey]?.values[columnKey] as
                | TableRecordValue
                | undefined;
            const columnDef = tableData.definition[columnKey];

            const isNewColumnCell = typeof prevColumnDef === 'undefined';
            const isNewRecordCell = typeof prevCellValue === 'undefined';
            const isNewCell = isNewColumnCell || isNewRecordCell;
            // If column types dont match or their value comparison fails, we consider the cell to be unequal.
            const unequalCells =
                prevColumnDef?.type !== columnDef.type ||
                (!isNewRecordCell &&
                    !columnSpecs[columnDef.type].compare(prevCellValue, cellValue));

            // If a new cell is added or the value has changed, we add it to the diff map.
            if (isNewCell || unequalCells) {
                const recordDiffMap =
                    diff.recordValues.get(recordKey) ||
                    diff.recordValues.set(recordKey, new Map()).get(recordKey);
                assertIsDefined(recordDiffMap);
                recordDiffMap.set(columnKey, {
                    before:
                        typeof prevColumnDef !== 'undefined' && typeof prevCellValue !== 'undefined'
                            ? {
                                  definition: prevColumnDef,
                                  value: prevCellValue,
                              }
                            : undefined,
                    after: cellValue,
                });
            }
        });
    });

    return modified.merge({
        fragments: diffedFragments,
        data: modified.data.merge({
            diff,
        }),
    });
}
